// src/views/PortalAppointmentDetail.js
// PHASE 1.5 VERIFIER: r2-pad-8c4d2e91
//
// Customer-portal view for a single appointment (job). Renders job header
// info plus the chronological list of customer-facing notes attached to
// this job. Reachable from PortalAppointments.js by tapping any card.
//
// Data path:
//   - Appointment: the getCustomerAppointments callable ({ jobId }) —
//     s6portal-7b3e9d52. The portal has NO direct read of job documents
//     (the rule was deleted in S6a; a job carries staff labour cost rates).
//   - Notes: portalsafe-7ca145c4 — the SAME getCustomerAppointments call returns
//     the visit's customer-facing notes, cut down on the server to text, author
//     name, date and a has-photos flag (functions/utils/portalAppointments.js).
//     This page used to read the note documents straight from Firestore and so
//     received the author's staff id, the ids and titles of everything a note is
//     attached to, and photo storage paths. The history below is kept for context.
//
// Round 2 rules-mirror fix:
//   Round 1 queried by attachedIndex array-contains 'job:{jobId}' but
//   the Firestore rule requires publicAttachments array-contains customerId.
//   Firestore rejected the query at the rules layer because the
//   security-critical constraint wasn't proven by the query. Fix: query
//   by publicAttachments (the rule-required constraint), filter by
//   attachedIndex client-side. Firestore allows only one array-contains
//   per query, so this is the cleanest mirror.
//
// Defense in depth — four layers prevent internal note leakage:
//   1. Firestore rules forbid portal reads of non-customer-facing notes
//   2. Query filters visibility == 'customer' server-side
//   3. Query filters publicAttachments == customerId server-side
//   4. Render code below double-checks visibility before drawing each note
//
// Legacy techNotes merge:
//   The job document carries a `techNotes` array (pre-Phase 1.1 system).
//   Customer-facing entries from that array are merged into the same
//   render path as new polymorphic notes — same visual treatment, sorted
//   together by addedAt. Note: legacy techNotes carry the load until a
//   job-context NoteComposer surface ships in a future phase.

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase/config'; // portalsafe-7ca145c4 — no Firestore reads on this page
import { Loader2, ArrowLeft, Calendar, MapPin, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { getJobArrivalWindowLabel } from '../utils/arrivalWindows'; // portalwin-3e7a2c95

// portalwin-3e7a2c95 — what this page should say about WHEN. Kept identical in
// wording and precedence to PortalAppointments.js, because the two pages show the
// same appointment and a customer moves between them: the list card and the detail
// header must never disagree about what they were promised. See the long note in
// PortalAppointments.js for why the clock time was wrong on ~95% of jobs and why
// this resolves per-org labels from the job's snapshot rather than fetching the
// org doc into the portal.
const resolveScheduleLine = (job, timeStr) => {
  const windowLabel = getJobArrivalWindowLabel(job);
  if (job?.schedulingMode === 'window' && windowLabel) {
    return { label: 'Arrival window', value: windowLabel };
  }
  if (job?.isAllDay === true) {
    return { label: 'Scheduled', value: 'All day' };
  }
  return { label: 'Scheduled time', value: timeStr };
};

// Safe conversion from Firestore Timestamp / Date / ISO string / number to Date.
// Legacy techNotes may store addedAt as a number, string, or Date; new
// polymorphic notes use serverTimestamp() so they arrive as Firestore Timestamp.
const toDateSafe = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const PortalAppointmentDetail = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAppointment = async () => {
      const user = auth.currentUser;
      if (!user || !jobId) {
        setLoading(false);
        return;
      }

      try {
        const tokenResult = await user.getIdTokenResult();
        const orgId = tokenResult.claims.organizationId;
        const customerId = tokenResult.claims.customerId;

        if (!orgId || !customerId) {
          setLoading(false);
          return;
        }

        // --- Read 1: the appointment, through the server projection ---
        // s6portal-7b3e9d52 — S6a (security audit #2). This used to be a direct
        // getDoc of the job, which delivered the WHOLE document — every worker's
        // labour cost rate included — to the customer's browser. The portal read
        // rule on jobs is now deleted. getCustomerAppointments enforces, on the
        // server, everything this page used to check here: the job belongs to
        // the customer on the login token, and its eventType is a customer-facing
        // one (Job / Appointment; a missing eventType is denied). Another
        // customer's job, an internal event and a missing job all come back as
        // the same not-found, which lands in the catch below and renders
        // "Appointment not found." Only the fields this page shows are returned.
        const getAppointments = httpsCallable(functions, 'getCustomerAppointments');
        const result = await getAppointments({ jobId });
        const appointment = result.data?.appointment;
        if (!appointment) {
          setLoading(false);
          return;
        }

        setJob(appointment);

        // --- Notes: came with the appointment (portalsafe-7ca145c4) ---
        // Already filtered on the server to customer-facing notes published to
        // this customer and attached to this visit, newest first — the same set
        // and order the old direct query + client-side filter produced.
        setNotes(Array.isArray(result.data?.notes) ? result.data.notes : []);
      } catch (error) {
        console.error('PortalAppointmentDetail: error loading appointment', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [jobId]);

  if (loading) {
    return (
      <div className="p-10 flex justify-center">
        <Loader2 className="animate-spin text-green-600" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-3xl">
        <Link
          to="/portal/appointments"
          className="flex items-center text-gray-500 hover:text-gray-800 transition-colors mb-4"
        >
          <ArrowLeft size={18} className="mr-1" /> Back to Appointments
        </Link>
        <div className="bg-white p-10 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-gray-500">Appointment not found.</p>
        </div>
      </div>
    );
  }

  const startDate = toDateSafe(job.startAt);
  const dateStr = startDate
    ? startDate.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Date TBD';
  const timeStr = startDate
    ? startDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : '';
  const address = job.location || job.address || '';
  const schedule = resolveScheduleLine(job, timeStr); // portalwin-3e7a2c95

  return (
    <div className="max-w-3xl">
      {/* Back link */}
      <Link
        to="/portal/appointments"
        className="flex items-center text-gray-500 hover:text-gray-800 transition-colors mb-4"
      >
        <ArrowLeft size={18} className="mr-1" /> Back to Appointments
      </Link>

      {/* Header card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
            <Calendar size={24} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{dateStr}</h1>
            {/* portalwin-3e7a2c95 — gated on the resolved value, not on timeStr:
                a windowed job must still show its window even though the clock
                time it used to print is now deliberately unused. */}
            {schedule.value && (
              <p className="text-gray-500 text-sm mb-2">{schedule.label}: {schedule.value}</p>
            )}
            {address && (
              <div className="flex items-center gap-2 text-gray-600 mt-3 bg-gray-50 p-2 rounded-md inline-block">
                <MapPin size={16} />
                <span className="text-sm font-medium">{address}</span>
              </div>
            )}
            {job.title && (
              <h2 className="text-base font-semibold text-gray-700 mt-4">{job.title}</h2>
            )}
            {job.description && (
              <p className="text-gray-600 text-sm whitespace-pre-wrap mt-2">
                {job.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Notes section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare size={20} className="text-gray-500" />
          <h2 className="text-lg font-bold text-gray-800">
            Notes from your service provider
          </h2>
        </div>

        {notes.length === 0 ? (
          <p className="text-gray-500 italic">
            No notes from your service provider yet.
          </p>
        ) : (
          <div className="space-y-3">
            {notes.map((note, idx) => {
              // portalsafe-7ca145c4 — visibility is enforced on the server; the
              // projected note carries no visibility field to recheck here.
              const noteDate = toDateSafe(note.addedAt);

              return (
                <div
                  key={idx}
                  className="bg-blue-50 border border-blue-100 rounded-md p-4"
                >
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <span className="font-semibold text-gray-700">
                      {note.authorName || 'Team'}
                    </span>
                    {noteDate && (
                      <>
                        <span>·</span>
                        <span>{format(noteDate, 'MMM d, yyyy · h:mm a')}</span>
                      </>
                    )}
                  </div>
                  <p className="text-gray-700 whitespace-pre-line break-words">
                    {note.text}
                  </p>
                  {/* Media scaffold — empty until Phase 1.7-1.9 ships uploads */}
                  {note.hasMedia && (
                    <div className="mt-3 text-xs text-gray-400 italic">
                      (Media attachments coming soon.)
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalAppointmentDetail;